import { useState, useCallback } from "react";
import JSZip from "jszip";
import { parseHarelFiles, isHarelContents } from "../parsers/harelParser";

// Loads Harel files — either individual raw files (RP*, SB*, RM*.R etc.)
// or a ZIP archive that contains them. Matches members to existing clients.
export function useHarelBatchLoader() {
  const [busy, setBusy] = useState(false);

  const loadFiles = useCallback(async (files, existingClients) => {
    setBusy(true);
    const results = [];

    const allFiles = Array.from(files);
    const zips = allFiles.filter(f => f.name.toLowerCase().endsWith('.zip'));
    const raws = allFiles.filter(f => !f.name.toLowerCase().endsWith('.zip'));

    // ── groups to parse ────────────────────────────────────────────
    // Each group: { sourceName, entries: [{ name, content: ArrayBuffer }] }
    const groups = [];

    // Individual raw files (RP/SB/RM/etc. without extension) → one group
    if (raws.length > 0) {
      const entries = await Promise.all(
        raws.map(async f => ({ name: f.name, content: await f.arrayBuffer() }))
      );
      // Display name from the 2-digit month in the filename (year chosen at import).
      let sourceName = raws[0].name;
      for (const f of raws) {
        const m = f.name.match(/\.(\d{2})R?$/i) || f.name.match(/(\d{2})$/);
        if (m && +m[1] >= 1 && +m[1] <= 12) { sourceName = `הראל · חודש ${m[1]}`; break; }
      }
      groups.push({ sourceName, entries });
    }

    // ZIP archives → peek inside; only accept if they look like Harel
    for (const zipFile of zips) {
      try {
        const zip = await JSZip.loadAsync(zipFile);
        const entries = await Promise.all(
          Object.values(zip.files)
            .filter(e => !e.dir)
            .map(async e => ({
              name: e.name.replace(/.*\//, ''),
              content: await e.async('arraybuffer'),
            }))
        );
        if (isHarelContents(entries)) {
          groups.push({ sourceName: zipFile.name, entries });
        } else {
          results.push({ fileName: zipFile.name, error: 'הקובץ אינו מכיל קבצי הראל (חסר RP/RB)' });
        }
      } catch (err) {
        results.push({ fileName: zipFile.name, error: err.message });
      }
    }

    // ── parse each group and match members ─────────────────────────
    for (const group of groups) {
      try {
        const parsed = parseHarelFiles(group.entries);

        const matched   = [];
        const unmatched = [];

        for (const member of parsed.members) {
          const existing = existingClients.find(c => c.clientId === member.taz);
          if (existing) {
            matched.push({ ...member, existingClientId: existing.id, existingName: existing.clientName });
          } else {
            // Pre-fill name from the parsed personal records
            const newName = [member.firstName, member.lastName].filter(Boolean).join(' ');
            unmatched.push({ ...member, newName });
          }
        }

        // Year isn't in the filename → default to current year + parsed month;
        // the import review lets the user adjust before confirming.
        const mm = parsed.reportMonth || String(new Date().getMonth() + 1).padStart(2, "0");
        const defaultReportMonth = `${new Date().getFullYear()}-${mm}`; // "YYYY-MM"

        results.push({
          fileName: group.sourceName,
          reportMonth: parsed.reportMonth,
          reportDate: `${defaultReportMonth}-15`,
          defaultReportMonth,
          matched,
          unmatched,
          totalBalance: parsed.members.reduce(
            (sum, m) => sum + m.funds.reduce((s, f) => s + f.balance, 0), 0
          ),
        });
      } catch (err) {
        results.push({ fileName: group.sourceName, error: err.message });
      }
    }

    setBusy(false);
    return results;
  }, []);

  return { loadFiles, busy };
}
